import { Trigger } from './rtm-trigger';
import { TemplatePosition } from './template-position';

export type ValidNonScheduledType = '.Email' | '.RTMOverlayChannel' | '.RTMInjectedChannel' | '.SMS' | '.WhatsApp';
export type ValidOffsiteNonScheduledType = '.Email';

export interface BaseChannel {
  type: ValidNonScheduledType | '.ScheduledChannel';
}

export type OnSiteChannel = RTMInjectedChannel | RTMOverlayChannel;
export type OffSiteChannel = EmailChannel | SMSChannel | WhatsAppChannel;
export type TemplateChannel = OnSiteChannel | OffSiteChannel;

export interface EmailChannel extends BaseChannel {
  templateId?: string;
  type: '.Email';
  delay?: number;
  subjectLine?: string;
  cycleId?: string;
}

export interface SMSChannel extends BaseChannel {
  type: '.SMS';
  templateId?: string;
  delay?: number;
  subjectLine?: string;
  cycleId?: string;
}

export interface WhatsAppChannel extends BaseChannel {
  templateId?: string;
  type: '.WhatsApp';
  delay?: number;
  subjectLine?: string;
  cycleId?: string;
}

export interface RTMOverlayChannel extends BaseChannel {
  rtmTrigger?: Trigger[];
  templateId?: string;
  type: '.RTMOverlayChannel';
}

export interface RTMInjectedChannel extends BaseChannel {
  rtmTrigger?: Trigger[];
  selector?: string;
  templateId?: string;
  templateInjectedPosition?: TemplatePosition;
  type: '.RTMInjectedChannel';
}

export const isInjectedChannel = (channel: Channel): channel is RTMInjectedChannel =>
  channel.type === '.RTMInjectedChannel';
export const isOnSiteChannel = (channel: Channel): channel is OnSiteChannel =>
  channel.type === '.RTMInjectedChannel' || channel.type === '.RTMOverlayChannel';
export const isOffSiteChannel = (channel: Channel): channel is OffSiteChannel => !isOnSiteChannel(channel);

// legacy dynamo Storage format for back compatibility
export interface ScheduledChannel extends BaseChannel {
  channel?: OffSiteChannel;
  duration?: number;
  type: '.ScheduledChannel';
  templateId?: string;
}

// Stores offline Channels in legacy dynamo Storage format for back compatibility
export function getStorageChannel(emailChannel: EmailChannel): EmailChannel | ScheduledChannel {
  if (emailChannel.delay > 0) {
    const delay = emailChannel.delay;
    emailChannel.delay = null; //remove before saving for dynamoDb back compatibility
    const scheduledChannel: ScheduledChannel = {
      type: '.ScheduledChannel',
      channel: emailChannel,
      duration: delay,
    };
    return scheduledChannel;
  } else {
    emailChannel.delay = null; //do not store in dynamoDb
    return emailChannel;
  }
}

// Convert legacy dynamo Storage format to OfflineChannel
export function getEmailChannel(scheduledChannel: ScheduledChannel): EmailChannel {
  const emailChannel: EmailChannel = {
    type: '.Email',
    templateId: scheduledChannel.channel.templateId,
    subjectLine: scheduledChannel.channel.subjectLine,
    delay: scheduledChannel.duration ?? 0,
    cycleId: scheduledChannel.channel.cycleId,
  };
  return emailChannel;
}

export type Channel =
  | EmailChannel
  | RTMOverlayChannel
  | RTMInjectedChannel
  | ScheduledChannel
  | SMSChannel
  | WhatsAppChannel;

// Delayed Channels
export type DelayChannel = EmailChannel | ScheduledChannel;
